<template>
  <v-row class="ma-0 pa-0 optimization-response-chart-wrapper">
    <v-col cols="12" class="ma-0 pa-0 optimization-response-chart-cell">
      <v-chart
        class="optimization-response-chart"
        :option="chartOption"
        autoresize
        @click="handleChartClick"
      />
    </v-col>
  </v-row>
</template>

<script>
import VChart, { THEME_KEY } from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent,
} from "echarts/components";
import { normalizeForecastingChannelName } from "../forecastingChannelColors";

use([
  CanvasRenderer,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  MarkLineComponent,
  TooltipComponent,
]);

export default {
  name: "OptimizationResponseCurveChart",
  components: {
    VChart,
  },
  provide: {
    [THEME_KEY]: "light",
  },
  props: {
    chartData: {
      type: Array,
      default: () => [],
    },
    colors: {
      type: Array,
      default: () => [],
    },
    channelColors: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    chartOption() {
      const header = this.chartData[0];

      if (!Array.isArray(header) || this.chartData.length < 2) {
        return {};
      }

      const rows = this.chartData.slice(1);
      const series = [];
      const legendData = [];
      const formatCompactNumber = this.formatCompactNumber;
      const formatCurrency = this.formatCurrency;
      const chartFontFamily = "Poppins";
      const axisLabelFontSize = 15;
      const axisTitleFontSize = 20;
      const legendFontSize = 14;
      const tooltipFontSize = 16;
      const dataZoomFontSize = 13;

      for (let columnIndex = 1; columnIndex < header.length; columnIndex++) {
        const seriesName = header[columnIndex];

        if (!seriesName || this.isMarkerColumn(seriesName)) {
          continue;
        }

        const color = this.getSeriesColor(seriesName, legendData.length);
        const markerColumnIndex = this.getMarkerColumnIndex(
          header,
          columnIndex,
          seriesName
        );
        const markerPoint = this.getMarkerPoint(rows, markerColumnIndex);
        const lineData = rows
          .filter((row) => row[columnIndex] != null)
          .map((row) => [Number(row[0]), Number(row[columnIndex])])
          .filter((point) => {
            return !Number.isNaN(point[0]) && !Number.isNaN(point[1]);
          });

        legendData.push(seriesName);
        series.push({
          name: seriesName,
          type: "line",
          smooth: true,
          showSymbol: false,
          symbol: "circle",
          symbolSize: 7,
          cursor: "pointer",
          triggerLineEvent: true,
          data: lineData,
          itemStyle: {
            color,
          },
          lineStyle: {
            color,
            width: 2,
          },
          emphasis: {
            focus: "series",
            itemStyle: {
              borderColor: "#ffffff",
              borderWidth: 2,
            },
            scale: true,
          },
          markLine: markerPoint
            ? {
                animation: false,
                data: [
                  [
                    { coord: [markerPoint[0], markerPoint[1]] },
                    { coord: [markerPoint[0], 0] },
                  ],
                  [
                    { coord: [markerPoint[0], markerPoint[1]] },
                    { coord: [0, markerPoint[1]] },
                  ],
                ],
                label: {
                  show: false,
                },
                lineStyle: {
                  color,
                  type: "dashed",
                  width: 2,
                },
                symbol: ["circle", "arrow"],
                symbolSize: 8,
              }
            : undefined,
        });
      }

      return {
        animationDuration: 500,
        color: this.colors,
        grid: {
          left: "3%",
          right: "3%",
          top: 40,
          bottom: 80,
          containLabel: true,
        },
        legend: {
          data: legendData,
          type: "scroll",
          top: 0,
          left: "center",
          textStyle: {
            fontFamily: chartFontFamily,
            fontSize: legendFontSize,
          },
        },
        tooltip: {
          trigger: "axis",
          order: "valueAsc",
          textStyle: {
            fontFamily: chartFontFamily,
            fontSize: tooltipFontSize,
          },
          formatter(params) {
            const activeParams = params.filter((item) => {
              return item.seriesType === "line" && Array.isArray(item.value);
            });
            let xValue = 0;

            if (activeParams.length > 0 && activeParams[0].value) {
              xValue = activeParams[0].value[0];
            } else if (params.length > 0) {
              xValue = params[0].axisValue;
            }

            const body = activeParams
              .map((item) => {
                return `${item.marker}${item.seriesName}: ${formatCurrency(item.value[1])}`;
              })
              .join("<br/>");

            return `<strong>Budget: ${formatCurrency(xValue)}</strong><br/>${body}`;
          },
        },
        xAxis: {
          type: "value",
          min: 0,
          name: "Budget ($)",
          nameLocation: "middle",
          nameGap: 30,
          nameTextStyle: {
            fontFamily: chartFontFamily,
            fontSize: axisTitleFontSize,
            fontWeight: 500,
          },
          axisLabel: {
            formatter(value) {
              return formatCompactNumber(value);
            },
            fontFamily: chartFontFamily,
            fontSize: axisLabelFontSize,
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: "value",
          min: 0,
          name: "Volume ($)",
          nameLocation: "middle",
          nameGap: 48,
          nameTextStyle: {
            fontFamily: chartFontFamily,
            fontSize: axisTitleFontSize,
            fontWeight: 500,
          },
          axisLabel: {
            formatter(value) {
              return formatCompactNumber(value);
            },
            fontFamily: chartFontFamily,
            fontSize: axisLabelFontSize,
          },
          splitLine: {
            show: false,
          },
        },
        dataZoom: [
          {
            type: "slider",
            xAxisIndex: 0,
            start: 0,
            end: 100,
            bottom: 12,
            height: 28,
            textStyle: {
              fontFamily: chartFontFamily,
              fontSize: dataZoomFontSize,
            },
          },
          {
            type: "inside",
            xAxisIndex: 0,
            start: 0,
            end: 100,
          },
        ],
        series,
      };
    },
  },
  methods: {
    isMarkerColumn(columnName) {
      const label = String(columnName);

      return label === "Point" || label.indexOf("Point ") === 0;
    },
    getMarkerColumnIndex(header, columnIndex, seriesName) {
      const namedMarkerIndex = header.indexOf(`Point ${seriesName}`);

      if (namedMarkerIndex >= 0) {
        return namedMarkerIndex;
      }

      if (
        columnIndex + 1 < header.length &&
        String(header[columnIndex + 1]).indexOf("Point") === 0
      ) {
        return columnIndex + 1;
      }

      return -1;
    },
    getSeriesColor(seriesName, seriesIndex) {
      if (this.channelColors[seriesName]) {
        return this.channelColors[seriesName];
      }

      const normalizedSeriesName = normalizeForecastingChannelName(seriesName);
      if (this.channelColors[normalizedSeriesName]) {
        return this.channelColors[normalizedSeriesName];
      }

      if (this.colors.length === 0) {
        return undefined;
      }

      return this.colors[seriesIndex % this.colors.length];
    },
    getMarkerPoint(rows, markerColumnIndex) {
      if (markerColumnIndex < 0) {
        return null;
      }

      const markerRows = rows
        .filter((row) => row[markerColumnIndex] != null)
        .map((row) => [Number(row[0]), Number(row[markerColumnIndex])])
        .filter((point) => {
          return !Number.isNaN(point[0]) && !Number.isNaN(point[1]);
        });

      if (markerRows.length === 0) {
        return null;
      }

      return markerRows.reduce((maxPoint, point) => {
        return point[1] > maxPoint[1] ? point : maxPoint;
      }, markerRows[0]);
    },
    formatCompactNumber(value) {
      return new Intl.NumberFormat("en-US", {
        notation: "compact",
        compactDisplay: "short",
        maximumFractionDigits: 1,
      }).format(Number(value));
    },
    formatCurrency(value) {
      return `$${this.formatCompactNumber(value)}`;
    },
    handleChartClick(params) {
      if (
        params.componentType !== "series" ||
        params.seriesType !== "line" ||
        (!Array.isArray(params.value) && !Array.isArray(params.data))
      ) {
        return;
      }
      const selectedValue = Array.isArray(params.value) ? params.value : params.data;

      this.$emit("response-curve-point-select", {
        channel: params.seriesName,
        spend: Number(selectedValue[0]),
        volume: Number(selectedValue[1]),
      });
    },
  },
};
</script>

<style scoped>
.optimization-response-chart-wrapper,
.optimization-response-chart-cell,
.optimization-response-chart {
  height: 100%;
  width: 100%;
}
</style>
